import { createSlice, PayloadAction } from '@reduxjs/toolkit';

// Define the state type
interface UiState {
  theme: 'light' | 'dark';
  sidebarOpen: boolean;
  activeModal: string | null;
  notifications: Array<{
    id: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'error';
  }>;
  sidebarCollapsed: boolean;
  messageLogging: {
    isLoggingPanelOpen: boolean;
  };
  lastUpdate: number; // Timestamp to trigger component re-renders
}

// Initial state
// NOTE: lastUpdate uses 0 instead of Date.now() for SSR hydration compatibility.
// Date.now() would produce different values on server vs client, causing hydration mismatches.
// The forceUpdate action can be used client-side to set a real timestamp when needed.
const initialState: UiState = {
  theme: 'light',
  sidebarOpen: true,
  activeModal: null,
  notifications: [],
  sidebarCollapsed: false,
  messageLogging: {
    isLoggingPanelOpen: false,
  },
  lastUpdate: 0,
};

// Create the slice
const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    setTheme(state, action: PayloadAction<'light' | 'dark'>) {
      state.theme = action.payload;
    },
    toggleSidebar(state) {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    setSidebarCollapsed(state, action: PayloadAction<boolean>) {
      state.sidebarCollapsed = action.payload;
    },
    openModal(state, action: PayloadAction<string>) {
      state.activeModal = action.payload;
    },
    closeModal(state) {
      state.activeModal = null;
    },
    addNotification(state, action: PayloadAction<Omit<UiState['notifications'][0], 'id'>>) {
      const id = Date.now().toString();
      state.notifications.push({
        id,
        ...action.payload,
      });
    },
    removeNotification(state, action: PayloadAction<string>) {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      );
    },
    // Message logging UI actions
    toggleLoggingPanel(state) {
      state.messageLogging.isLoggingPanelOpen = !state.messageLogging.isLoggingPanelOpen;
    },
    setLoggingPanelOpen(state, action: PayloadAction<boolean>) {
      state.messageLogging.isLoggingPanelOpen = action.payload;
    },
    // Add a force update action to trigger UI re-renders
    forceUpdate(state, action: PayloadAction<number>) {
      state.lastUpdate = action.payload;
    },
  },
});

// Export actions and reducer
export const {
  setTheme,
  toggleSidebar,
  setSidebarCollapsed,
  openModal,
  closeModal,
  addNotification,
  removeNotification,
  toggleLoggingPanel,
  setLoggingPanelOpen,
  forceUpdate
} = uiSlice.actions;

export default uiSlice.reducer; 